import { useEffect, useRef, useState } from "react";
import { Search, MapPin, Loader2, LocateFixed } from "lucide-react";
import { toast } from "sonner";

/**
 * Free US address autocomplete via the OpenStreetMap-based Photon API (no API key).
 * Returns a structured address object on select.
 */
export function AddressAutocomplete({ onSelect, autoFocus = false, testid = "address-search" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [locating, setLocating] = useState(false);
  const boxRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (query.trim().length < 4) {
      setResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        let feats = [];
        try {
          const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=6&lang=en`;
          const res = await fetch(url);
          const data = await res.json();
          feats = (data.features || []).filter((f) => {
            const c = f.properties?.countrycode;
            return c === "US" && (f.properties?.housenumber || f.properties?.street || f.properties?.name);
          });
        } catch {
          feats = [];
        }
        // Fallback to Nominatim (OpenStreetMap) if Photon is empty/rate-limited.
        if (feats.length === 0) {
          try {
            const nurl = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&countrycodes=us&limit=6&q=${encodeURIComponent(query)}`;
            const nres = await fetch(nurl, { headers: { Accept: "application/json" } });
            const ndata = await nres.json();
            feats = (ndata || []).map((r) => {
              const a = r.address || {};
              const street = [a.house_number, a.road].filter(Boolean).join(" ");
              return {
                geometry: { coordinates: [parseFloat(r.lon), parseFloat(r.lat)] },
                properties: {
                  countrycode: "US",
                  housenumber: a.house_number,
                  street: a.road,
                  name: r.display_name?.split(",")[0],
                  city: a.city || a.town || a.village || a.hamlet || a.county,
                  state: a.state,
                  postcode: a.postcode,
                },
                _display: r.display_name,
              };
            });
          } catch {
            feats = [];
          }
        }
        setResults(feats);
        setOpen(true);
        setActive(-1);
      } finally {
        setLoading(false);
      }
    }, 320);
  }, [query]);

  const parse = (f) => {
    const p = f.properties || {};
    const [lng, lat] = f.geometry?.coordinates || [];
    const street = [p.housenumber, p.street || p.name].filter(Boolean).join(" ");
    const cityPart = p.city || p.town || p.village || p.county || "";
    const full = [street, cityPart, [p.state, p.postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return {
      full,
      street,
      city: cityPart,
      state: p.state || "",
      zip: p.postcode || "",
      lat,
      lng,
      label: full || p.name,
    };
  };

  const pick = (f) => {
    const addr = parse(f);
    setQuery(addr.full);
    setOpen(false);
    onSelect?.(addr);
  };

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Location isn't supported on this device.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat=${lat}&lon=${lng}`;
          const res = await fetch(url, { headers: { Accept: "application/json" } });
          const data = await res.json();
          const a = data.address || {};
          if (a.country_code && a.country_code !== "us") {
            toast.error("We currently only cover US properties.");
            return;
          }
          const street = [a.house_number, a.road].filter(Boolean).join(" ");
          const city = a.city || a.town || a.village || a.hamlet || a.county || "";
          const full = [street, city, [a.state, a.postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
          const addr = {
            full: full || data.display_name || "",
            street,
            city,
            state: a.state || "",
            zip: a.postcode || "",
            lat,
            lng,
            label: full || data.display_name,
            source: "gps",
            accuracy: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null,
          };
          setQuery(addr.full);
          setOpen(false);
          onSelect?.(addr);
          toast.success("Located you — confirm the property below.");
        } catch {
          toast.error("Couldn't look up your address. Try typing it instead.");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        const msg =
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied. Enable it in your browser settings or type the address."
            : err.code === err.TIMEOUT
            ? "Locating timed out. Please try again."
            : "Couldn't get your location. Try typing the address.";
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(results[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <label htmlFor="addr-input" className="label-mtr">Property address</label>
      <div className="relative">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar pointer-events-none" aria-hidden="true" />
        <input
          id="addr-input"
          data-testid={`${testid}-input`}
          className="input-mtr pl-12 pr-11"
          placeholder="Enter a US property address…"
          value={query}
          autoFocus={autoFocus}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={onKey}
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {loading && <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-orange animate-spin" aria-hidden="true" />}
      </div>

      <button
        type="button"
        onClick={useMyLocation}
        disabled={locating}
        data-testid={`${testid}-gps-btn`}
        className="mt-2.5 inline-flex items-center gap-2 text-[14px] font-semibold text-orange hover:text-orange/80 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {locating ? <Loader2 size={16} className="animate-spin" /> : <LocateFixed size={16} />}
        {locating ? "Locating…" : "Use my current location"}
      </button>
      <p className="text-[12px] text-mortar mt-1">On site? Tap to find the property you're standing at, then confirm it.</p>

      {open && results.length > 0 && (
        <ul
          className="absolute z-30 mt-2 w-full bg-white rounded-input border-2 border-gravel shadow-card overflow-hidden"
          data-testid={`${testid}-results`}
          role="listbox"
        >
          {results.map((f, i) => {
            const a = parse(f);
            return (
              <li key={i} role="option" aria-selected={active === i}>
                <button
                  type="button"
                  data-testid={`${testid}-option-${i}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(f)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 ${active === i ? "bg-fieldstone" : "bg-white"}`}
                >
                  <MapPin size={18} className="text-orange mt-0.5 shrink-0" aria-hidden="true" />
                  <span className="text-[15px] text-iron leading-snug">{a.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;
